if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          mains: this.querySelectorAll(".product-media-main"),
          thumbnails: this.querySelectorAll(
            ".product-media-thumbnail:not(.product-media-thumbnail-variant), .product-media-thumbnail:first-child",
          ),
          slideshow: this.querySelector(".product-media-main-wrapper"),
        };

        if (!this.elements.thumbnails.length) {
          return;
        }

        this.elements.thumbnails.forEach((thumbnail) => {
          const mediaToggle = thumbnail.querySelector("button");
          if (!mediaToggle) {
            return;
          }
          mediaToggle.addEventListener(
            "click",
            this.setActiveMedia.bind(this, mediaToggle.dataset.mediaId, false),
          );
        });
      }

      connectedCallback() {
        if (!window.Flickity) {
          return;
        }

        this.elements.slideshow.style.display = "block";
        this.flickity = new Flickity(this.elements.slideshow, {
          autoPlay: false,
          cellAlign: "center",
          groupCells: false,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          cellSelector: ".product-media",
          initialIndex: 0,
          pageDots: false,
          wrapAround: true,
          accessibility: false,
          adaptiveHeight: true,
          watchCSS: true,
          on: {
            change: (index) => {
              const { element } = this.flickity.cells[index];
              const mediaId = element.dataset.mediaId;
              this.setActiveMedia(mediaId);
            },
          },
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMainMedia = [...this.elements.mains].find((element) => {
          return element.classList.contains("is-active");
        });
        const nextMainMedia = [...this.elements.mains].find((element) => {
          return element.dataset.mediaId === mediaId.toString();
        });
        const index = [...this.elements.mains].indexOf(nextMainMedia);

        if (this.flickity) {
          this.flickity.select(index);
        }

        this.elements.thumbnails.forEach((thumb) => {
          thumb.classList.remove("is-active");
        });
        const thumbnail = [...this.elements.thumbnails].find((element) => {
          return element.dataset.mediaId === mediaId.toString();
        });

        activeMainMedia.classList.remove("is-active");

        nextMainMedia.classList.add("is-active");
        thumbnail?.classList.add("is-active");
        this.playActiveMedia(nextMainMedia);

        if (!thumbnail) {
          // The code below is for thumbnails, return early
          // if there are not visible (e.g. in the quick view drawer).
          return;
        }

        if (prepend) {
          thumbnail.parentElement.prepend(thumbnail);
        } else {
          const container = thumbnail.parentElement;
          const containerRect = container.getBoundingClientRect();
          const thumbnailRect = thumbnail.getBoundingClientRect();

          const isHorizontalScroll = container.scrollWidth > container.clientWidth;

          if (isHorizontalScroll) {
            if (thumbnailRect.left < containerRect.left) {
              container.scrollBy({
                left: thumbnailRect.left - containerRect.left,
                behavior: "smooth",
              });
            } else if (thumbnailRect.right > containerRect.right) {
              container.scrollBy({
                left: thumbnailRect.right - containerRect.right,
                behavior: "smooth",
              });
            }
          } else {
            if (thumbnailRect.top < containerRect.top) {
              container.scrollBy({
                top: thumbnailRect.top - containerRect.top,
                behavior: "smooth",
              });
            } else if (thumbnailRect.bottom > containerRect.bottom) {
              container.scrollBy({
                top: thumbnailRect.bottom - containerRect.bottom,
                behavior: "smooth",
              });
            }
          }
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector("deferred-media");

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }
    },
  );
}

if (!customElements.get("product-thumbnail-navigation")) {
  customElements.define(
    "product-thumbnail-navigation",
    class ProductThumbnailNavigation extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.gallery = document.querySelector(`#${this.getAttribute("for")}`);
        this.elements = {
          gallery: this.gallery,
          scroller: this.gallery.querySelector(".product-media-scroller"),
          thumbnailWrapper: this.gallery.querySelector(".product-media-thumbnails"),
          thumbnails: this.gallery.querySelectorAll(".product-media-thumbnail"),
          buttonPrev: this.querySelector(".js-prev"),
          buttonNext: this.querySelector(".js-next"),
        };

        if (!this.elements.thumbnails.length) {
          return;
        }

        if (this.elements.scroller.clientHeight < this.elements.thumbnailWrapper.scrollHeight) {
          this.classList.remove("hidden");

          this.elements.buttonPrev.addEventListener("click", (event) => {
            event.preventDefault();
            this.prev();
          });

          this.elements.buttonNext.addEventListener("click", (event) => {
            event.preventDefault();
            this.next();
          });

          this.elements.thumbnailWrapper.addEventListener("scroll", () => {
            this.buttonAvailability();
          });
        }
      }

      prev() {
        const scrollPos = this.elements.thumbnailWrapper.scrollTop;
        this.elements.thumbnailWrapper.scroll(0, scrollPos - 300);
      }

      next() {
        const scrollPos = this.elements.thumbnailWrapper.scrollTop;
        this.elements.thumbnailWrapper.scroll(0, scrollPos + 300);
      }

      buttonAvailability() {
        const el = this.elements.thumbnailWrapper;
        const hasBottomed = el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
        const hasTopped = el.scrollTop <= 30;
        this.elements.buttonPrev.classList.remove("pill-nav-item-disabled");
        this.elements.buttonNext.classList.remove("pill-nav-item-disabled");

        if (hasBottomed) {
          this.elements.buttonNext.classList.add("pill-nav-item-disabled");
        }

        if (hasTopped) {
          this.elements.buttonPrev.classList.add("pill-nav-item-disabled");
        }
      }
    },
  );
}

/*
 * In-box zoom (desktop mouse only)
 * --------------------------------
 * Hovering the main photo magnifies it inside its own frame and follows the mouse, so nothing ever
 * covers the price, title or buttons. Clicking opens the full-screen viewer (see product-modal.js).
 * Touch screens never trigger the hover zoom; a tap opens the viewer instead.
 */
if (!customElements.get("media-zoom")) {
  customElements.define(
    "media-zoom",
    class MediaZoom extends HTMLElement {
      constructor() {
        super();

        this.mode = this.getAttribute("data-mode");
        this.zoomScale = parseFloat(this.getAttribute("data-zoom-scale")) || 2.5;
        this.hovering = false;
        this.pointer = null;
        this.frame = 0;

        this.onPointerEnter = this.onPointerEnter.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.original = this.querySelector("img");

        if (!this.original) {
          return;
        }

        this.original.addEventListener("pointerenter", this.onPointerEnter);
        this.original.addEventListener("pointermove", this.onPointerMove);
        this.original.addEventListener("pointerleave", this.onPointerLeave);
        this.original.addEventListener("click", this.onClick);
      }

      disconnectedCallback() {
        if (!this.original) {
          return;
        }

        this.original.removeEventListener("pointerenter", this.onPointerEnter);
        this.original.removeEventListener("pointermove", this.onPointerMove);
        this.original.removeEventListener("pointerleave", this.onPointerLeave);
        this.original.removeEventListener("click", this.onClick);
        cancelAnimationFrame(this.frame);
        this.frame = 0;
      }

      isMouse(event) {
        return event.pointerType === "mouse" || event.pointerType === "pen";
      }

      // The magnified copy sits on top of the photo, inside the same frame.
      createLens() {
        const lens = document.createElement("div");
        lens.className = "media-zoom-lens";
        lens.setAttribute("aria-hidden", "true");

        const inner = document.createElement("div");
        inner.className = "media-zoom-lens-inner";
        inner.style.setProperty("--media-zoom-scale", this.zoomScale);

        const objectFit = getComputedStyle(this.original).objectFit;
        const makeImage = (src) => {
          const image = document.createElement("img");
          image.setAttribute("data-zoom-lens", "");
          image.alt = "";
          image.decoding = "async";
          image.style.objectFit = objectFit;
          image.src = src;
          return image;
        };

        const soft = makeImage(this.original.currentSrc || this.original.src);

        inner.appendChild(soft);
        lens.appendChild(inner);
        this.original.insertAdjacentElement("afterend", lens);

        this.lens = lens;
        this.lensInner = inner;

        // The magnified photo is shown only once it is ready, so the first hover never flashes blank.
        this.lensReady = soft.decode ? soft.decode().catch(() => {}) : Promise.resolve();

        // Swap in the larger photo once it has downloaded and decoded (the store never enlarges
        // small originals).
        const largeSrc = this.getAttribute("data-zoom");

        if (largeSrc) {
          const large = makeImage(largeSrc);
          const swap = () => {
            inner.appendChild(large);
            soft.remove();
          };

          if (large.decode) {
            large.decode().then(swap, () => {});
          } else {
            large.addEventListener("load", swap, { once: true });
          }
        }
      }

      // The point under the mouse stays put while the photo grows around it.
      setOrigin() {
        const rect = this.original.getBoundingClientRect();

        if (!rect.width || !rect.height || !this.pointer) {
          return;
        }

        const x = Math.min(Math.max((this.pointer.x - rect.left) / rect.width, 0), 1) * 100;
        const y = Math.min(Math.max((this.pointer.y - rect.top) / rect.height, 0), 1) * 100;

        this.lensInner.style.transformOrigin = `${x.toFixed(1)}% ${y.toFixed(1)}%`;
      }

      onPointerEnter(event) {
        if (!this.isMouse(event)) {
          return;
        }

        if (!this.lens) {
          this.createLens();
        }

        this.hovering = true;
        this.pointer = { x: event.clientX, y: event.clientY };
        this.setOrigin();

        this.lensReady.then(() => {
          if (this.hovering) {
            this.lens.classList.add("is-active");
          }
        });
      }

      onPointerMove(event) {
        if (!this.hovering || !this.isMouse(event)) {
          return;
        }

        this.pointer = { x: event.clientX, y: event.clientY };

        if (!this.frame) {
          this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.setOrigin();
          });
        }
      }

      onPointerLeave() {
        this.hovering = false;

        if (this.lens) {
          this.lens.classList.remove("is-active");
        }
      }

      onClick() {
        const sectionId = this.getAttribute("data-modal-id");
        const modal = document.querySelector(`#ProductModal-${sectionId}`);

        if (modal) {
          modal.show(this);
        }
      }
    },
  );
}

/*
 * Sticky product details (desktop)
 * --------------------------------
 * The buy box can be taller than the screen, so a plain "stick to the top" leaves its lower part
 * out of reach. Instead the details move with the page in whichever direction you scroll, and hold
 * still when their bottom edge reaches the bottom of the screen (scrolling down) or their top edge
 * reaches the top gap (scrolling up) while the photos keep scrolling. Short details simply stick to
 * the top, as before.
 */
(function () {
  const info = document.querySelector(".product-page .product-info");

  if (!info) {
    return;
  }

  const desktop = window.matchMedia("(min-width: 990px)");
  const topGap = 20;
  const bottomGap = 20;
  let top = topGap;
  let lastY = window.scrollY;
  let applied = null;
  let frame = 0;

  function apply(value) {
    if (value !== applied) {
      applied = value;

      if (value === null) {
        info.style.removeProperty("--product-info-top");
      } else {
        info.style.setProperty("--product-info-top", `${value}px`);
      }
    }
  }

  function update() {
    frame = 0;

    const y = window.scrollY;

    if (!desktop.matches) {
      top = topGap;
      lastY = y;
      apply(null);
      return;
    }

    const minTop = Math.min(topGap, window.innerHeight - info.offsetHeight - bottomGap);

    top = Math.max(minTop, Math.min(topGap, top - (y - lastY)));
    lastY = y;
    apply(Math.round(top));
  }

  function schedule() {
    if (!frame) {
      frame = requestAnimationFrame(update);
    }
  }

  if ("ResizeObserver" in window) {
    new ResizeObserver(schedule).observe(info);
  }

  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule, { passive: true });
  schedule();
})();